import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Col, Row, Card, Modal, Dropdown, Button, Space, Popconfirm, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { RiArrowLeftLine, RiCloseFill } from 'react-icons/ri';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { MdOutlineUnpublished } from 'react-icons/md';
import { DownOutlined } from '@ant-design/icons';
import useNavigationSource from '../../../../../app/hooks/useNavigationSource';
import { TYPE_DICTIONARY } from '../../../../TemplateBuilder/const/templates';
import { Permissions } from '@/const/permissions';
import { HasRights } from '@/components/HasRights/HasRights';
import appRoutes from '@/configs/routes';
import handleResponse from '../../../../../lib/handleResponse';
import getConfigurations from '../../../../../lib/getConfigurations';
import mkPreparedTemplateData from '../../../utils/mkPreparedTemplateData';
import useToggle from '../../../../../app/hooks/useToggle';
import Spinner from '../../../../../components/Spinner';
import { ActionButton, BreadCrumbs, IntlMessages } from '@/components';
import ContentCard from '../../../components/ContentCard';
import CollectionDetailsCard from '../../../components/CollectionDetailsCard';
import ImageModalCardBodyView from '../../../components/ImageModalCard/ImageModalCardBodyView';
import EmptyDataPlaceholder from '../../../../../components/EmptyDataPlaceholder';
import getFeatureSourceTranslation from '../../../../Featured/utils/getFeatureSourceTranslation';
import { SOURCES_MAP } from '@/const/sources';
import { useGetDictionaryQuery } from '../../../../Translations/api/dictionariesApiSlice';
import { useGetLanguagesQuery } from '../../../../Translations/api/languagesApiSlice';
import {
  useDeleteCollectionMutation,
  useGetSpecificCollectionQuery,
  useUnpublishCollectionMutation,
} from '../../../api/collectionsApiSlice';

const PageCollectionDetails = () => {
  const intl = useIntl();
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [previewModalOpen, togglePreviewModal] = useToggle();
  const [previewImage, setPreviewImage] = useState({});
  const { urlPrefix, navigationSource } = useNavigationSource(SOURCES_MAP.COLLECTIONS);

  const { data: collectionDetails = {}, isLoading } = useGetSpecificCollectionQuery({ id: collectionId, include: 'events' });
  const dictionaryField = collectionDetails?.dynamic_data?.data?.find((item) => item.type === TYPE_DICTIONARY);

  const { data: collectionDictionary = {} } = useGetDictionaryQuery(
    {
      id: dictionaryField?.value,
      include: 'translations,structure,structure.fields.translations',
    },
    { skip: !dictionaryField?.value },
  );

  const { data: { data: languages = [] } = { data: [] } } = useGetLanguagesQuery(
    { queryParams: 'limit=0' },
    { skip: !dictionaryField },
  );

  const data = mkPreparedTemplateData({
    templateData: collectionDetails?.template?.data?.fields?.data,
    dynamicData: collectionDetails?.dynamic_data?.data,
    dictionary: collectionDictionary,
    languages,
  });

  const [deleteCollection, { isLoading: isCollectionDeleting }] = useDeleteCollectionMutation();
  const [unpublishCollection, { isLoading: isUnpublishCollectionLoading }] = useUnpublishCollectionMutation();

  const images = collectionDetails?.images?.data ?? [];
  const collectionConfig = getConfigurations(SOURCES_MAP.COLLECTIONS);
  const allowUpdatePast = collectionConfig?.publish?.allow_update_past ?? true;
  const releaseDate = collectionDetails?.released_at;
  const allowUpdate = allowUpdatePast
    ? true
    : dayjs().isSameOrBefore(dayjs(releaseDate).subtract(-1, 'days'), 'day');
  const allowUnpublishPast = collectionConfig?.publish?.allow_unpublish_past ?? true;
  const allowUnpublish = allowUnpublishPast
    ? Boolean(collectionDetails?.is_published)
    : Boolean(collectionDetails?.is_published) && dayjs().isSameOrBefore(dayjs(releaseDate), 'day');
  const isUnpublishBlockedByEvent = allowUnpublish && Boolean(collectionDetails?.events?.data?.length);

  const isLoadingData = isLoading || isCollectionDeleting || isUnpublishCollectionLoading;

  useEffect(() => {
    const { hash } = window.location;

    if (hash) {
      const element = document.querySelector(hash);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [collectionDictionary, collectionDetails.images?.data]);

  const handleUnpublish = () => {
    if (!collectionId || isUnpublishBlockedByEvent) {
      return;
    }

    unpublishCollection(collectionId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const handleDelete = () => {
    deleteCollection(collectionId)
      .unwrap()
      .then(() => {
        handleResponse('success', intl, 'ui-general-deleted-successfully', [
          () => navigate(appRoutes.urlPageCollections()),
        ]);
      })
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleCardClick = (item) => {
    setPreviewImage(item);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };

  const allowRenderActions =
    (allowUpdate && HasRights([Permissions.CONTENT.COLLECTIONS.EDIT])) ||
    (allowUnpublish && HasRights([Permissions.CONTENT.COLLECTIONS.EDIT])) ||
    (!collectionDetails.is_published &&
      collectionDetails.images?.data.length === 0 &&
      HasRights([Permissions.CONTENT.COLLECTIONS.DELETE]));

  const actionItems = [
    allowUpdate && HasRights([Permissions.CONTENT.COLLECTIONS.EDIT])
      ? {
        key: 'edit',
        icon: <PiPencilSimpleLine data-testid="action-edit-icon" size={18} />,
        label: <IntlMessages id="ui-general-edit" />,
        onClick: () =>
          navigate(appRoutes.urlPageCollectionEdit({ urlPrefix, source: navigationSource, collectionId })),
      }
      : null,

    allowUnpublish && HasRights([Permissions.CONTENT.COLLECTIONS.UNPUBLISH])
      ? isUnpublishBlockedByEvent
        ? {
          className: 'menu-item-disabled',
          key: 'unpublish',
          icon: <MdOutlineUnpublished data-testid="action-unpublish-icon" size={18} />,
          label: (
            <Tooltip placement="top" title={<IntlMessages id="collection-unpublish-used-notification" />}>
              <span>
                <IntlMessages id="ui-general-unpublish" />
              </span>
            </Tooltip>
          ),
          onClick: ({ domEvent }) => domEvent.preventDefault(),
        }
        : {
          key: 'unpublish',
          icon: <MdOutlineUnpublished data-testid="action-unpublish-icon" size={18} />,
          label: <IntlMessages id="ui-general-unpublish" />,
          onClick: handleUnpublish,
        }
      : null,

    !collectionDetails.is_published &&
    collectionDetails.images?.data.length === 0 &&
    HasRights([Permissions.CONTENT.COLLECTIONS.DELETE])
      ? {
        key: 'delete',
        icon: <PiTrashSimple data-testid="action-delete-icon" size={18} />,
        label: (
          <Popconfirm
            title={<IntlMessages id="ui-general-delete-confirm-message" />}
            placement="topLeft"
            okButtonProps={{ danger: true }}
            onConfirm={handleDelete}
          >
            <span>
              <IntlMessages id="ui-general-delete" />
            </span>
          </Popconfirm>
        ),
        onClick: ({ domEvent }) => domEvent.stopPropagation(),
      }
      : null,
  ].filter(Boolean);

  return (
    <>
      <Modal
        width={1200}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        open={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <ImageModalCardBodyView data={previewImage} />
      </Modal>

      <Row gutter={[32, 32]} justify="space-between" className="hp-mb-12" align="middle">
        <BreadCrumbs
          breadCrumbParent={
            urlPrefix ? (
              <Link to={appRoutes.urlPageFeatures({ featureSource: urlPrefix })}>
                <IntlMessages id={getFeatureSourceTranslation(urlPrefix)} />
              </Link>
            ) : null
          }
          breadCrumbParent2={
            <Link to={appRoutes.urlPageCollections({ urlPrefix, source: navigationSource })}>
              <IntlMessages id="collections-breadcrumbs" />
            </Link>
          }
          breadCrumbActive={collectionId}
        />

        <Col md={10} span={24} className="text-end">
          <Space size="middle">
            {allowRenderActions && (
              <Dropdown menu={{ items: actionItems }}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size="middle">
                    <IntlMessages id="ui-general-action" />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}

            <ActionButton
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(appRoutes.urlPageCollections({ urlPrefix, source: navigationSource }))}
              title={<IntlMessages id="ui-general-back-to-list" />}
            />
          </Space>
        </Col>
      </Row>

      <Spinner spinning={isLoadingData}>
        <Row gutter={[32, 32]} className="hp-mb-32">
          <Col span={24}>
            <CollectionDetailsCard
              data={collectionDetails}
              collectionDictionary={collectionDictionary}
              languages={languages}
              templateCardsData={data}
            />
          </Col>
        </Row>

        <Row gutter={[32, 32]}>
          <Col span={24}>
            <Card
              id="attached-images"
              loading={isLoadingData}
              title={<IntlMessages id="achievements-attached-images" />}
            >
              <div className={images.length > 0 ? 'content-card-grid' : ''}>
                {images.length > 0 ? (
                  images.map((item) => {
                    return <ContentCard key={item.id} data={item} onClick={handleCardClick} />;
                  })
                ) : (
                  <EmptyDataPlaceholder placeholder={<IntlMessages id="ui-general-no-attached-images" />} />
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Spinner>
    </>
  );
};

export default PageCollectionDetails;
